import { useRef, useState, useCallback } from 'react'
import { resolveApiPath } from '@/lib/apiBaseUrl'

export interface StreamMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
}

export interface StreamOptions {
  endpoint?: string
  apiKey: string
  model: string
  messages: StreamMessage[]
  temperature?: number
  max_tokens?: number
  top_p?: number
  stream?: boolean
  onDelta?: (delta: string, full: string) => void
  onDone?: (full: string) => void
  onError?: (err: Error) => void
  signal?: AbortSignal
}

/**
 * 兼容 OpenAI chat/completions 流式响应
 * 解析 `data: {...}\n\n` 形式的 SSE
 */
export async function streamChat(options: StreamOptions): Promise<string> {
  const {
    endpoint = '/v1/chat/completions',
    apiKey,
    model,
    messages,
    temperature,
    max_tokens,
    top_p,
    stream = true,
    onDelta,
    onDone,
    onError,
    signal,
  } = options

  const requestUrl = /^https?:\/\//i.test(endpoint) ? endpoint : resolveApiPath(endpoint)

  try {
    const res = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model,
        messages,
        temperature,
        max_tokens,
        top_p,
        stream,
      }),
      signal,
    })

    if (!res.ok) {
      const text = await res.text()
      let msg = text
      try {
        const json = JSON.parse(text)
        msg = json?.error?.message || json?.message || text
      } catch {
        // keep raw text
      }
      throw new Error(msg || `HTTP ${res.status}`)
    }

    if (!stream) {
      const json = await res.json()
      const content: string = json?.choices?.[0]?.message?.content || ''
      onDone?.(content)
      return content
    }

    const reader = res.body?.getReader()
    if (!reader) throw new Error('浏览器不支持流式读取')

    const decoder = new TextDecoder('utf-8')
    let buffer = ''
    let full = ''

    let streamClosed = false
    while (!streamClosed) {
      const { value, done } = await reader.read()
      if (done) {
        streamClosed = true
        break
      }
      buffer += decoder.decode(value, { stream: true })

      let idx
      while ((idx = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, idx).trim()
        buffer = buffer.slice(idx + 1)
        if (!line.startsWith('data:')) continue
        const payload = line.slice(5).trim()
        if (payload === '[DONE]') {
          onDone?.(full)
          return full
        }
        try {
          const json = JSON.parse(payload)
          const delta: string = json?.choices?.[0]?.delta?.content || ''
          if (delta) {
            full += delta
            onDelta?.(delta, full)
          }
        } catch {
          // ignore malformed chunks
        }
      }
    }
    onDone?.(full)
    return full
  } catch (err) {
    const e = err instanceof Error ? err : new Error(String(err))
    onError?.(e)
    throw e
  }
}

export function useStream() {
  const [loading, setLoading] = useState(false)
  const [content, setContent] = useState('')
  const abortRef = useRef<AbortController | null>(null)

  const start = useCallback(
    async (options: Omit<StreamOptions, 'signal' | 'onDelta' | 'onDone' | 'onError'> & {
      onDelta?: (delta: string, full: string) => void
      onDone?: (full: string) => void
      onError?: (err: Error) => void
    }) => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      setLoading(true)
      setContent('')
      try {
        const result = await streamChat({
          ...options,
          signal: controller.signal,
          onDelta: (delta, full) => {
            setContent(full)
            options.onDelta?.(delta, full)
          },
          onDone: (full) => {
            setContent(full)
            options.onDone?.(full)
          },
          onError: (err) => {
            options.onError?.(err)
          },
        })
        return result
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const stop = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setLoading(false)
  }, [])

  const reset = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setContent('')
    setLoading(false)
  }, [])

  return { loading, content, start, stop, reset }
}
